﻿import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/axios';
import BarcodeScanner from '../components/BarcodeScanner';
import Layout from '../components/Layout';
import ImageUpload from '../components/ImageUpload';
import { SkeletonTable } from '../components/Skeleton';
import { usePermissions } from '../hooks/usePermissions';
import { formatCurrency as fmt } from '../utils/currency';
import { resolveTierPrice } from '../utils/pricing';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
const token = () => localStorage.getItem('token');

const EMPTY = {
  name: '', description: '', quantity: 0, price: '', cost: '', sku: '', category: '',
  expiryDate: '', reorderLevel: 10, reorderQuantity: 50, supplierId: '',
  imageUrl: '', imagePath: '',
  wholesalePrice: '', vipPrice: '',
  baseUnitName: 'Unit', baseUnitAllowPurchase: true, defaultSaleUnitKey: 'base', units: [],
};

const ADJUSTMENT_REASONS = {
  increase: [
    { id: 'count_gain', label: 'Stock count gain' },
    { id: 'found_stock', label: 'Found stock' },
    { id: 'manual_correction', label: 'Manual correction' },
    { id: 'opening_balance', label: 'Opening balance' },
  ],
  decrease: [
    { id: 'damage', label: 'Damaged stock' },
    { id: 'expiry_writeoff', label: 'Expired / write-off' },
    { id: 'supplier_return', label: 'Returned to supplier' },
    { id: 'count_loss', label: 'Stock count loss' },
    { id: 'manual_correction', label: 'Manual correction' },
  ],
};

const createUnitKey = () => `unit-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;

const createExtraUnit = () => ({
  key: createUnitKey(),
  name: '',
  conversionQty: '',
  sellingPrice: '',
  wholesalePrice: '',
  vipPrice: '',
  costPrice: '',
  sku: '',
  allowSale: true,
  allowPurchase: false,
});

const hydrateUnits = (item) => {
  const units = Array.isArray(item.units) ? item.units : [];
  const baseUnit = item.baseUnit || units.find((unit) => unit.isBaseUnit) || null;
  const extraUnits = units
    .filter((unit) => !unit.isBaseUnit)
    .map((unit) => ({
      key: `unit-${unit.id || createUnitKey()}`,
      name: unit.name || '',
      conversionQty: unit.conversionQty ?? '',
      sellingPrice: unit.sellingPrice ?? '',
      wholesalePrice: unit.wholesalePrice ?? '',
      vipPrice: unit.vipPrice ?? '',
      costPrice: unit.costPrice ?? '',
      sku: unit.sku || '',
      allowSale: unit.allowSale !== false,
      allowPurchase: Boolean(unit.allowPurchase),
    }));
  const defaultSaleUnit = item.defaultSaleUnit || units.find((unit) => unit.isDefaultSaleUnit) || baseUnit;

  return {
    baseUnitName: baseUnit?.name || 'Unit',
    baseUnitAllowPurchase: baseUnit?.allowPurchase ?? true,
    defaultSaleUnitKey: defaultSaleUnit?.isBaseUnit ? 'base' : `unit-${defaultSaleUnit?.id}`,
    wholesalePrice: baseUnit?.wholesalePrice ?? item.price ?? '',
    vipPrice: baseUnit?.vipPrice ?? baseUnit?.wholesalePrice ?? item.price ?? '',
    units: extraUnits,
  };
};

function StockBar({ quantity, reorderLevel = 10 }) {
  const pct = quantity === 0 ? 0 : Math.min((quantity / Math.max(reorderLevel * 3, 50)) * 100, 100);
  const color = pct < 20 ? '#EF4444' : pct < 50 ? '#F59E0B' : '#22C55E';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 110 }}>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: '#E2E8F0', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3, background: color, transition: 'width 0.6s ease' }} />
      </div>
      <span style={{ fontSize: '0.72rem', fontWeight: 700, color, minWidth: 28, textAlign: 'right' }}>
        {quantity}
      </span>
      {quantity <= reorderLevel && quantity > 0 && (
        <span style={{ fontSize: '0.62rem', fontWeight: 700, padding: '1px 6px', borderRadius: 20, background: '#FEE2E2', color: '#991B1B', display: 'flex', alignItems: 'center', gap: 3 }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#EF4444', animation: 'pulse-ring 1.5s infinite', display: 'inline-block' }} />
          Low
        </span>
      )}
    </div>
  );
}

function ExpiryBadge({ date }) {
  if (!date) return <span style={{ color: '#CBD5E1', fontSize: '0.78rem' }}>—</span>;
  const days = Math.ceil((new Date(date) - Date.now()) / 86_400_000);
  const label = new Date(date).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' });
  const color = days < 0 ? '#DC2626' : days < 7 ? '#E11D48' : days < 30 ? '#D97706' : days < 90 ? '#CA8A04' : '#16A34A';
  const bg    = days < 0 ? '#FEF2F2' : days < 30 ? '#FFF7ED' : '#F0FDF4';
  return (
    <div>
      <div style={{ fontSize: '0.78rem', color, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '0.68rem', color, background: bg, padding: '1px 6px', borderRadius: 10, marginTop: 2, display: 'inline-block' }}>
        {days < 0 ? `${Math.abs(days)}d ago` : `${days}d left`}
      </div>
    </div>
  );
}

function getItemStatus(item) {
  const reorderLevel = item.reorderLevel ?? 10;
  if (item.expiryDate) {
    const expiryDays = Math.ceil((new Date(item.expiryDate) - Date.now()) / 86_400_000);
    if (expiryDays < 0) return { label: 'Expired', tone: 'red' };
    if (expiryDays <= 7) return { label: 'Expiring', tone: 'amber' };
  }
  if (item.quantity === 0) return { label: 'Out', tone: 'red' };
  if (item.quantity <= reorderLevel) return { label: 'Low', tone: 'amber' };
  return { label: 'In stock', tone: 'green' };
}

function getMarginPercent(item, canViewInventoryCosts = true) {
  if (!canViewInventoryCosts || item.cost == null) return null;
  const price = Number(item.price) || 0;
  const cost = Number(item.cost) || 0;
  if (price <= 0) return null;
  return ((price - cost) / price) * 100;
}

function getItemStockValue(item, canViewInventoryCosts = true) {
  if (!canViewInventoryCosts || item.cost == null) return 0;
  return (Number(item.cost) || 0) * (Number(item.quantity) || 0);
}

function adjustmentReasonLabel(reason) {
  return String(reason || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function productMonogram(name) {
  const cleaned = String(name || '')
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .trim();
  if (!cleaned) return 'IT';
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function priceMatchesBand(price, band) {
  if (band === 'all') return true;
  const value = Number(price) || 0;
  if (band === 'under20') return value < 20;
  if (band === '20to50') return value >= 20 && value <= 50;
  if (band === 'over50') return value > 50;
  return true;
}

const INVENTORY_STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Cabinet+Grotesk:wght@400;500;700;800;900&family=JetBrains+Mono:wght@400;600;700&display=swap');

.inventory-redesign-shell {
  --ink: #0B0E17;
  --surface: #1E2235;
  --surface2: #252A3D;
  --surface3: #2A3050;
  --gold: #F0B429;
  --gold2: #FFCD3C;
  --teal: #00C9B1;
  --coral: #FF6B6B;
  --lavender: #A78BFA;
  --sky: #38BDF8;
  --white: #FFFFFF;
  --muted: #6B7A99;
  --muted2: #3D4559;
  --border: rgba(255,255,255,0.06);
  --border2: rgba(255,255,255,0.1);
  --success-bg: rgba(0,201,177,0.12);
  --warn-bg: rgba(240,180,41,0.12);
  --danger-bg: rgba(255,107,107,0.12);
  position: relative;
  overflow: hidden;
  padding: 28px 32px 32px;
  border-radius: 28px;
  background: var(--ink);
  color: var(--white);
  font-family: 'Cabinet Grotesk', sans-serif;
  box-shadow: 0 28px 80px rgba(11,14,23,0.35);
}

.inventory-redesign-shell::before,
.inventory-redesign-shell::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.inventory-redesign-shell::before {
  background:
    radial-gradient(ellipse 720px 520px at 6% 0%, rgba(27,58,107,0.55) 0%, transparent 66%),
    radial-gradient(ellipse 420px 420px at 96% 72%, rgba(240,180,41,0.07) 0%, transparent 66%),
    radial-gradient(ellipse 320px 320px at 52% 100%, rgba(0,201,177,0.05) 0%, transparent 66%);
}

.inventory-redesign-shell::after {
  background-image:
    linear-gradient(rgba(255,255,255,0.012) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.012) 1px, transparent 1px);
  background-size: 44px 44px;
  opacity: 0.45;
}

.inventory-redesign-inner {
  position: relative;
  z-index: 1;
  display: grid;
  gap: 18px;
}

.inventory-redesign-topbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
}

.inventory-redesign-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--gold);
  margin-bottom: 10px;
}

.inventory-redesign-eyebrow::before {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--gold), var(--gold2));
  box-shadow: 0 0 0 6px rgba(240,180,41,0.12);
}

.inventory-redesign-title {
  margin: 0;
  font-size: clamp(30px, 3vw, 40px);
  font-weight: 900;
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.inventory-redesign-title span {
  color: var(--gold2);
}

.inventory-redesign-subtitle {
  margin-top: 10px;
  max-width: 720px;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.55;
}

.inventory-redesign-alerts {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.inventory-redesign-alert {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid var(--border2);
  background: rgba(255,255,255,0.04);
  color: var(--white);
  font-size: 12px;
  font-weight: 700;
}

.inventory-redesign-alert.is-info {
  background: rgba(56,189,248,0.12);
  border-color: rgba(56,189,248,0.22);
  color: var(--sky);
}

.inventory-redesign-alert.is-warn {
  background: rgba(240,180,41,0.12);
  border-color: rgba(240,180,41,0.22);
  color: var(--gold);
}

.inventory-redesign-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: currentColor;
}

.inventory-redesign-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.inventory-redesign-btn,
.inventory-redesign-icon-btn,
.inventory-redesign-chip,
.inventory-redesign-tab,
.inventory-redesign-modal button,
.inventory-redesign-modal select,
.inventory-redesign-modal input,
.inventory-redesign-modal textarea {
  font-family: 'Cabinet Grotesk', sans-serif;
}

.inventory-redesign-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: rgba(255,255,255,0.05);
  color: var(--white);
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  transition: 160ms ease;
}

.inventory-redesign-btn:hover {
  transform: translateY(-1px);
  border-color: var(--border2);
  background: rgba(255,255,255,0.08);
}

.inventory-redesign-btn.is-gold {
  border: none;
  background: linear-gradient(135deg, var(--gold), var(--gold2));
  color: var(--ink);
  box-shadow: 0 14px 30px rgba(240,180,41,0.26);
}

.inventory-redesign-btn.is-gold:hover {
  box-shadow: 0 18px 34px rgba(240,180,41,0.32);
}

.inventory-redesign-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.inventory-redesign-kpi {
  position: relative;
  overflow: hidden;
  min-height: 138px;
  padding: 16px;
  border-radius: 18px;
  border: 1px solid var(--border);
  background: rgba(30,34,53,0.92);
  transition: transform 180ms ease, border-color 180ms ease;
}

.inventory-redesign-kpi:hover {
  transform: translateY(-2px);
  border-color: var(--border2);
}

.inventory-redesign-kpi::before {
  content: '';
  position: absolute;
  inset: 0 auto auto 0;
  width: 100%;
  height: 3px;
  background: var(--gold);
}

.inventory-redesign-kpi.is-gold::before { background: linear-gradient(90deg, var(--gold), var(--gold2)); }
.inventory-redesign-kpi.is-teal::before { background: linear-gradient(90deg, var(--teal), #00E5CC); }
.inventory-redesign-kpi.is-coral::before { background: linear-gradient(90deg, var(--coral), #FF9090); }
.inventory-redesign-kpi.is-lavender::before { background: linear-gradient(90deg, var(--lavender), #C4B5FD); }
.inventory-redesign-kpi.is-sky::before { background: linear-gradient(90deg, var(--sky), #7DD3FC); }

.inventory-redesign-kpi-label {
  margin-bottom: 10px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--muted);
}

.inventory-redesign-kpi-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: clamp(24px, 2.4vw, 30px);
  line-height: 1;
  font-weight: 700;
  letter-spacing: -0.06em;
}

.inventory-redesign-kpi-value.is-gold { color: var(--gold2); }
.inventory-redesign-kpi-value.is-teal { color: var(--teal); }
.inventory-redesign-kpi-value.is-coral { color: var(--coral); }
.inventory-redesign-kpi-value.is-lavender { color: var(--lavender); }
.inventory-redesign-kpi-value.is-sky { color: var(--sky); }

.inventory-redesign-kpi-sub {
  margin-top: 8px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
}

.inventory-redesign-kpi-mini {
  margin-top: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.inventory-redesign-kpi-mini span {
  padding: 5px 9px;
  border-radius: 999px;
  background: rgba(255,255,255,0.05);
  color: var(--white);
  font-size: 11px;
  font-weight: 700;
}

.inventory-redesign-kpi-icon {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 40px;
  height: 40px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: rgba(255,255,255,0.05);
  color: rgba(255,255,255,0.18);
  font-size: 14px;
  font-weight: 900;
}

.inventory-redesign-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.inventory-redesign-search {
  position: relative;
  flex: 1 1 280px;
}

.inventory-redesign-search input,
.inventory-redesign-select,
.inventory-redesign-modal input,
.inventory-redesign-modal textarea,
.inventory-redesign-modal select {
  width: 100%;
  min-height: 44px;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: rgba(37,42,61,0.92);
  color: var(--white);
  font-size: 14px;
  outline: none;
  transition: border-color 160ms ease, background 160ms ease;
}

.inventory-redesign-search input {
  padding-left: 42px;
}

.inventory-redesign-search input::placeholder,
.inventory-redesign-modal input::placeholder,
.inventory-redesign-modal textarea::placeholder {
  color: var(--muted);
}

.inventory-redesign-search input:focus,
.inventory-redesign-select:focus,
.inventory-redesign-modal input:focus,
.inventory-redesign-modal textarea:focus,
.inventory-redesign-modal select:focus {
  border-color: rgba(240,180,41,0.38);
  background: rgba(42,48,80,0.88);
}

.inventory-redesign-search svg {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
}

.inventory-redesign-select-wrap {
  flex: 0 1 180px;
}

.inventory-redesign-view-toggle {
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: rgba(255,255,255,0.04);
}

.inventory-redesign-icon-btn {
  width: 36px;
  height: 34px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: var(--muted);
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: 150ms ease;
}

.inventory-redesign-icon-btn.is-active {
  background: rgba(255,255,255,0.08);
  color: var(--white);
}

.inventory-redesign-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.inventory-redesign-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: rgba(255,255,255,0.03);
  color: var(--muted);
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  transition: 150ms ease;
}

.inventory-redesign-tab.is-active {
  background: rgba(240,180,41,0.12);
  border-color: rgba(240,180,41,0.3);
  color: var(--gold);
}

.inventory-redesign-tab.is-teal.is-active {
  background: rgba(0,201,177,0.12);
  border-color: rgba(0,201,177,0.28);
  color: var(--teal);
}

.inventory-redesign-tab.is-coral.is-active {
  background: rgba(255,107,107,0.12);
  border-color: rgba(255,107,107,0.28);
  color: var(--coral);
}

.inventory-redesign-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: rgba(255,255,255,0.08);
  color: currentColor;
  font-size: 10px;
  font-weight: 900;
}

.inventory-redesign-bulkbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(240,180,41,0.25);
  background: rgba(240,180,41,0.08);
}

.inventory-redesign-bulkbar strong {
  color: var(--gold2);
  font-size: 13px;
}

.inventory-redesign-bulkbar-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
  flex-wrap: wrap;
}

.inventory-redesign-section {
  border: 1px solid var(--border);
  border-radius: 20px;
  background: rgba(30,34,53,0.94);
  overflow: hidden;
}

.inventory-redesign-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-bottom: 1px solid var(--border);
  background: rgba(255,255,255,0.03);
}

.inventory-redesign-section-title {
  font-size: 18px;
  font-weight: 900;
  letter-spacing: -0.04em;
}

.inventory-redesign-section-meta {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  color: var(--muted);
  font-size: 12px;
  font-weight: 700;
}

.inventory-redesign-table-scroll {
  overflow-x: auto;
}

.inventory-redesign-table-scroll table {
  width: 100%;
  min-width: 1060px;
  border-collapse: collapse;
}

.inventory-redesign-table-scroll th {
  padding: 14px 16px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-align: left;
  color: var(--muted);
  border-bottom: 1px solid var(--border);
}

.inventory-redesign-table-scroll td {
  padding: 16px;
  border-bottom: 1px solid var(--border);
  vertical-align: middle;
}

.inventory-redesign-row {
  transition: background 150ms ease;
}

.inventory-redesign-row:hover {
  background: rgba(255,255,255,0.03);
}

.inventory-redesign-row.is-low {
  background: rgba(240,180,41,0.03);
}

.inventory-redesign-row.is-out {
  background: rgba(255,107,107,0.03);
}

.inventory-redesign-check {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1.5px solid var(--muted2);
  background: transparent;
  appearance: none;
  cursor: pointer;
  transition: 150ms ease;
}

.inventory-redesign-check:checked {
  background: var(--gold);
  border-color: var(--gold);
  box-shadow: inset 0 0 0 2px rgba(11,14,23,0.8);
}

.inventory-redesign-product {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 260px;
}

.inventory-redesign-thumb,
.inventory-redesign-thumb-empty {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  flex-shrink: 0;
}

.inventory-redesign-thumb {
  object-fit: cover;
  border: 1px solid var(--border);
}

.inventory-redesign-thumb-empty {
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, rgba(240,180,41,0.12), rgba(56,189,248,0.18));
  border: 1px solid var(--border);
  color: var(--white);
  font-size: 13px;
  font-weight: 900;
}

.inventory-redesign-product-name {
  font-size: 14px;
  font-weight: 800;
  color: var(--white);
}

.inventory-redesign-product-meta {
  margin-top: 3px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.inventory-redesign-sku,
.inventory-redesign-price,
.inventory-redesign-mono,
.inventory-redesign-stock-num,
.inventory-redesign-modal code {
  font-family: 'JetBrains Mono', monospace;
}

.inventory-redesign-sku {
  color: var(--muted);
  font-size: 11px;
}

.inventory-redesign-tag {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(255,255,255,0.06);
  color: var(--white);
  font-size: 10px;
  font-weight: 800;
}

.inventory-redesign-tag.is-blue {
  background: rgba(56,189,248,0.14);
  color: var(--sky);
}

.inventory-redesign-tag.is-muted {
  color: var(--muted);
}

.inventory-redesign-product-subline {
  margin-top: 6px;
  font-size: 11px;
  color: var(--muted);
  line-height: 1.5;
}

.inventory-redesign-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 900;
}

.inventory-redesign-status.is-green {
  background: var(--success-bg);
  color: var(--teal);
}

.inventory-redesign-status.is-amber {
  background: var(--warn-bg);
  color: var(--gold);
}

.inventory-redesign-status.is-red {
  background: var(--danger-bg);
  color: var(--coral);
}

.inventory-redesign-stock {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 120px;
}

.inventory-redesign-stock-bar {
  position: relative;
  width: 70px;
  height: 6px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255,255,255,0.08);
}

.inventory-redesign-stock-fill {
  height: 100%;
  border-radius: 999px;
}

.inventory-redesign-price {
  font-size: 13px;
  font-weight: 700;
  color: var(--gold2);
}

.inventory-redesign-cost {
  font-size: 13px;
  color: var(--muted);
}

.inventory-redesign-margin {
  font-size: 13px;
  font-weight: 800;
}

.inventory-redesign-margin.is-good { color: var(--teal); }
.inventory-redesign-margin.is-ok { color: var(--gold); }
.inventory-redesign-margin.is-thin { color: var(--coral); }
.inventory-redesign-margin.is-muted { color: var(--muted); }

.inventory-redesign-expiry {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  min-width: 92px;
}

.inventory-redesign-expiry-date {
  font-size: 12px;
  font-weight: 700;
}

.inventory-redesign-expiry-badge {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  min-height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 900;
}

.inventory-redesign-expiry-badge.is-green { background: rgba(0,201,177,0.12); color: var(--teal); }
.inventory-redesign-expiry-badge.is-amber { background: rgba(240,180,41,0.12); color: var(--gold); }
.inventory-redesign-expiry-badge.is-red { background: rgba(255,107,107,0.12); color: var(--coral); }

.inventory-redesign-actions-cell {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.inventory-redesign-action {
  min-height: 30px;
  padding: 0 10px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: rgba(255,255,255,0.05);
  color: var(--white);
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
  transition: 150ms ease;
}

.inventory-redesign-action:hover {
  background: rgba(255,255,255,0.09);
  border-color: var(--border2);
}

.inventory-redesign-action.is-danger:hover {
  background: rgba(255,107,107,0.12);
  border-color: rgba(255,107,107,0.26);
  color: var(--coral);
}

.inventory-redesign-action.is-ghost {
  color: var(--muted);
}

.inventory-redesign-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;
  padding: 18px;
}

.inventory-redesign-card {
  border: 1px solid var(--border);
  border-radius: 18px;
  background: rgba(37,42,61,0.88);
  padding: 16px;
  display: grid;
  gap: 12px;
}

.inventory-redesign-card-top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.inventory-redesign-card-prices {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.inventory-redesign-card-pricebox {
  border: 1px solid var(--border);
  border-radius: 14px;
  background: rgba(255,255,255,0.03);
  padding: 12px;
}

.inventory-redesign-card-pricebox label {
  display: block;
  margin-bottom: 6px;
  color: var(--muted);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.inventory-redesign-card-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.inventory-redesign-empty,
.inventory-redesign-error,
.inventory-redesign-loading {
  padding: 34px 24px;
  text-align: center;
  color: var(--muted);
}

.inventory-redesign-empty strong,
.inventory-redesign-error strong {
  display: block;
  margin-bottom: 8px;
  color: var(--white);
  font-size: 18px;
}

.inventory-redesign-loading-list {
  display: grid;
  gap: 10px;
  padding: 18px;
}

.inventory-redesign-loading-row {
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(90deg, rgba(255,255,255,0.04), rgba(255,255,255,0.09), rgba(255,255,255,0.04));
  background-size: 200% 100%;
  animation: inventory-shimmer 1.2s linear infinite;
}

@keyframes inventory-shimmer {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}

.inventory-redesign-activity {
  display: grid;
  gap: 10px;
  padding: 18px;
}

.inventory-redesign-activity-item {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 14px;
  border-radius: 16px;
  border: 1px solid var(--border);
  background: rgba(255,255,255,0.03);
}

.inventory-redesign-activity-icon {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  font-size: 16px;
  font-weight: 900;
}

.inventory-redesign-activity-icon.is-increase {
  background: rgba(0,201,177,0.16);
  color: var(--teal);
}

.inventory-redesign-activity-icon.is-decrease {
  background: rgba(255,107,107,0.14);
  color: var(--coral);
}

.inventory-redesign-activity-title {
  color: var(--white);
  font-size: 13px;
  font-weight: 800;
}

.inventory-redesign-activity-meta {
  margin-top: 3px;
  color: var(--muted);
  font-size: 11px;
  line-height: 1.45;
}

.inventory-redesign-activity-trail {
  color: var(--gold);
  font-size: 11px;
  font-weight: 900;
}

.inventory-redesign-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(3,6,14,0.76);
  backdrop-filter: blur(8px);
}

.inventory-redesign-modal {
  width: min(920px, 100%);
  max-height: calc(100vh - 48px);
  overflow: auto;
  border-radius: 24px;
  border: 1px solid var(--border2);
  background: linear-gradient(180deg, rgba(30,34,53,0.98), rgba(20,23,35,0.98));
  box-shadow: 0 30px 80px rgba(0,0,0,0.45);
}

.inventory-redesign-modal.is-compact {
  width: min(560px, 100%);
}

.inventory-redesign-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid var(--border);
}

.inventory-redesign-modal-title {
  margin: 0;
  font-size: 20px;
  font-weight: 900;
  letter-spacing: -0.04em;
}

.inventory-redesign-modal-subtitle {
  margin-top: 5px;
  color: var(--muted);
  font-size: 12px;
}

.inventory-redesign-close {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: rgba(255,255,255,0.04);
  color: var(--white);
  cursor: pointer;
  font-size: 16px;
  transition: 150ms ease;
}

.inventory-redesign-close:hover {
  background: rgba(255,107,107,0.12);
  border-color: rgba(255,107,107,0.25);
}

.inventory-redesign-modal-body {
  padding: 22px 24px 24px;
}

.inventory-redesign-alert-box {
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(255,107,107,0.25);
  background: rgba(255,107,107,0.12);
  color: #FFD2D2;
  font-size: 13px;
  font-weight: 700;
}

.inventory-redesign-form-grid {
  display: grid;
  gap: 16px;
}

.inventory-redesign-form-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.inventory-redesign-form-group {
  display: grid;
  gap: 8px;
}

.inventory-redesign-form-group.is-full {
  grid-column: 1 / -1;
}

.inventory-redesign-label {
  color: var(--muted);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.inventory-redesign-hint {
  color: var(--muted);
  font-size: 11px;
  line-height: 1.45;
}

.inventory-redesign-modal textarea {
  min-height: 92px;
  padding: 12px 14px;
  resize: vertical;
}

.inventory-redesign-form-panel {
  padding: 16px;
  border-radius: 18px;
  border: 1px solid var(--border);
  background: rgba(255,255,255,0.03);
}

.inventory-redesign-unit-card {
  padding: 14px;
  border-radius: 16px;
  border: 1px solid var(--border);
  background: rgba(11,14,23,0.26);
}

.inventory-redesign-panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.inventory-redesign-panel-copy {
  color: var(--muted);
  font-size: 12px;
  line-height: 1.45;
}

.inventory-redesign-radio,
.inventory-redesign-checkline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--white);
  font-size: 12px;
  font-weight: 700;
}

.inventory-redesign-radio input,
.inventory-redesign-checkline input {
  width: auto;
  min-height: auto;
  accent-color: var(--gold);
}

.inventory-redesign-inline-note {
  min-height: 44px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px dashed rgba(255,255,255,0.16);
  background: rgba(255,255,255,0.02);
  color: var(--muted);
  font-size: 12px;
  line-height: 1.45;
}

.inventory-redesign-upload {
  padding: 16px;
  border-radius: 18px;
  border: 1px solid var(--border);
  background: rgba(255,255,255,0.03);
}

.inventory-redesign-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 8px;
}

.inventory-redesign-spinner {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(11,14,23,0.16);
  border-top-color: var(--ink);
  animation: inventory-spin 0.7s linear infinite;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(3,6,14,0.76);
  backdrop-filter: blur(8px);
}

.modal {
  width: min(920px, 100%);
  max-height: calc(100vh - 48px);
  overflow: auto;
  border-radius: 24px;
  border: 1px solid var(--border2);
  background: linear-gradient(180deg, rgba(30,34,53,0.98), rgba(20,23,35,0.98));
  box-shadow: 0 30px 80px rgba(0,0,0,0.45);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid var(--border);
}

.modal-title {
  margin: 0;
  color: var(--white);
  font-size: 20px;
  font-weight: 900;
  letter-spacing: -0.04em;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.form-group {
  display: grid;
  gap: 8px;
  margin-bottom: 16px;
}

.form-label {
  color: var(--muted);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.form-input {
  width: 100%;
  min-height: 44px;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: rgba(37,42,61,0.92);
  color: var(--white);
  font-size: 14px;
  font-family: 'Cabinet Grotesk', sans-serif;
  outline: none;
}

.form-input:focus {
  border-color: rgba(240,180,41,0.38);
  background: rgba(42,48,80,0.88);
}

.form-input::placeholder {
  color: var(--muted);
}

textarea.form-input {
  min-height: 92px;
  padding: 12px 14px;
  resize: vertical;
}

.modal .btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 12px;
  font-family: 'Cabinet Grotesk', sans-serif;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  transition: 160ms ease;
}

.modal .btn-outline {
  border: 1px solid var(--border);
  background: rgba(255,255,255,0.05);
  color: var(--white);
}

.modal .btn-outline:hover {
  background: rgba(255,255,255,0.08);
}

.modal .btn-primary {
  border: none;
  background: linear-gradient(135deg, var(--gold), var(--gold2));
  color: var(--ink);
}

.modal .btn-danger {
  border: 1px solid rgba(255,107,107,0.24);
  background: rgba(255,107,107,0.12);
  color: var(--coral);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 8px;
}

.alert-error {
  margin: 18px 24px 0;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(255,107,107,0.25);
  background: rgba(255,107,107,0.12);
  color: #FFD2D2;
  font-size: 13px;
  font-weight: 700;
}

@keyframes inventory-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 980px) {
  .inventory-redesign-shell {
    padding: 22px 18px 24px;
    border-radius: 22px;
  }

  .inventory-redesign-topbar,
  .inventory-redesign-section-header,
  .inventory-redesign-panel-head {
    flex-direction: column;
    align-items: stretch;
  }

  .inventory-redesign-actions,
  .inventory-redesign-bulkbar-actions {
    justify-content: flex-start;
  }

  .inventory-redesign-form-row,
  .inventory-redesign-card-prices {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .inventory-redesign-controls {
    align-items: stretch;
  }

  .inventory-redesign-select-wrap,
  .inventory-redesign-search {
    flex-basis: 100%;
  }

  .inventory-redesign-grid {
    grid-template-columns: 1fr;
  }
}
`;

export default function Inventory() {
  const { can } = usePermissions();
  const canManageInventory = can('inventory:write');
  const canAdjustInventory = can('inventory:adjust');
  const canViewInventoryActivity = can('inventory:activity:view');
  const canViewInventoryCosts = can('inventory:cost:view');
  const [items, setItems]             = useState([]);
  const [suppliers, setSuppliers]     = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [search, setSearch]           = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all');
  const [viewMode, setViewMode]       = useState('table');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showModal, setShowModal]     = useState(false);
  const [editing, setEditing]         = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanHit, setScanHit]         = useState(null); // { barcode, source, brand }
  const [adjustingItem, setAdjustingItem] = useState(null);
  const [form, setForm]               = useState(EMPTY);
  const [adjustmentForm, setAdjustmentForm] = useState({
    unitId: '',
    direction: 'decrease',
    quantity: 1,
    reason: 'damage',
    notes: '',
  });
  const [error, setError]             = useState('');
  const [adjustmentError, setAdjustmentError] = useState('');
  const [loading, setLoading]         = useState(false);
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError]     = useState('');
  const [importState, setImportState] = useState(null); // { rows, summary, excluded: Set, result }
  const [importBusy, setImportBusy]   = useState(false);
  const [importError, setImportError] = useState('');
  const importInputRef = useRef(null);

  const load = useCallback(async () => {
    setPageLoading(true);
    setPageError('');
    try {
      const inventoryRequest = api.get('/inventory', { params: { search } });
      const adjustmentRequest = canViewInventoryActivity
        ? api.get('/stock-adjustments', { params: { limit: 8 } }).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] });

      const [{ data }, adjustmentsRes] = await Promise.all([
        inventoryRequest,
        adjustmentRequest,
      ]);
      setItems(data);
      setAdjustments(adjustmentsRes.data || []);
    } catch (error) {
      setPageError(error.response?.status === 403
        ? 'You do not have permission to view inventory.'
        : 'Failed to load inventory. Please try again.');
    } finally {
      setPageLoading(false);
    }
  }, [canViewInventoryActivity, search]);

  useEffect(() => { load(); }, [load]);

  const loadSuppliers = async () => {
    try {
      const r = await fetch(`${API}/suppliers`, { headers: { Authorization: `Bearer ${token()}` } });
      if (r.ok) setSuppliers(await r.json());
    } catch {
      setSuppliers([]);
    }
  };

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setFlag = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.checked }));

  const updateUnit = (unitKey, field, value) => {
    setForm((current) => ({
      ...current,
      units: current.units.map((unit) => (
        unit.key === unitKey
          ? {
              ...unit,
              [field]: value,
              ...(field === 'allowSale' && value === false && current.defaultSaleUnitKey === unitKey
                ? { allowSale: false }
                : {}),
            }
          : unit
      )),
      ...(field === 'allowSale' && value === false && current.defaultSaleUnitKey === unitKey
        ? { defaultSaleUnitKey: 'base' }
        : {}),
    }));
  };

  const addUnit = () => {
    setForm((current) => ({
      ...current,
      units: [...current.units, createExtraUnit()],
    }));
  };

  const removeUnit = (unitKey) => {
    setForm((current) => ({
      ...current,
      units: current.units.filter((unit) => unit.key !== unitKey),
      defaultSaleUnitKey: current.defaultSaleUnitKey === unitKey ? 'base' : current.defaultSaleUnitKey,
    }));
  };

  const openNew = () => {
    if (!canManageInventory) return;
    setEditing(null); setForm(EMPTY); setError(''); setShowModal(true);
    loadSuppliers();
  };

  const openEdit = (item) => {
    if (!canManageInventory) return;
    const unitState = hydrateUnits(item);
    setEditing(item);
    setForm({
      name: item.name, description: item.description || '',
      quantity: item.quantity, price: item.price, cost: item.cost,
      sku: item.sku || '', category: item.category || '',
      expiryDate: item.expiryDate ? new Date(item.expiryDate).toISOString().split('T')[0] : '',
      reorderLevel:    item.reorderLevel    ?? 10,
      reorderQuantity: item.reorderQuantity ?? 50,
      supplierId: item.supplierId ? String(item.supplierId) : '',
      imageUrl:  item.imageUrl  || '',
      imagePath: item.imagePath || '',
      wholesalePrice: item.baseUnit?.wholesalePrice ?? item.price ?? '',
      vipPrice: item.baseUnit?.vipPrice ?? item.baseUnit?.wholesalePrice ?? item.price ?? '',
      ...unitState,
    });
    setError('');
    setShowModal(true);
    loadSuppliers();
  };

  const handleBarcodeScan = async (barcode) => {
    setShowScanner(false);
    setScanLoading(true);
    setScanHit(null);
    try {
      const { data } = await api.get(`/barcode/${encodeURIComponent(barcode)}`);
      if (data.found) {
        setForm((f) => ({
          ...f,
          name: data.name || f.name,
          category: data.category || f.category,
          imageUrl: data.imageUrl || f.imageUrl,
        }));
        setScanHit({ barcode, source: data.source, brand: data.brand });
      } else {
        setScanHit({ barcode, source: null });
      }
    } catch {
      setScanHit({ barcode, source: null });
    } finally {
      setScanLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canManageInventory) return;
    setError('');
    setLoading(true);
    const payload = {
      ...form,
      quantity:        parseInt(form.quantity) || 0,
      price:           parseFloat(form.price)  || 0,
      wholesalePrice:  form.wholesalePrice,
      vipPrice:        form.vipPrice,
      cost:            parseFloat(form.cost)   || 0,
      reorderLevel:    parseInt(form.reorderLevel)    || 10,
      reorderQuantity: parseInt(form.reorderQuantity) || 50,
      supplierId:      form.supplierId ? parseInt(form.supplierId) : null,
      expiryDate:      form.expiryDate || null,
      baseUnitName:    form.baseUnitName?.trim() || 'Unit',
      baseUnitAllowPurchase: Boolean(form.baseUnitAllowPurchase),
      defaultSaleUnitKey: form.defaultSaleUnitKey || 'base',
      units: Array.isArray(form.units) ? form.units.map((unit) => ({
        key: unit.key,
        name: unit.name,
        conversionQty: parseInt(unit.conversionQty) || 0,
        sellingPrice: unit.sellingPrice,
        wholesalePrice: unit.wholesalePrice,
        vipPrice: unit.vipPrice,
        costPrice: unit.costPrice,
        sku: unit.sku,
        allowSale: unit.allowSale !== false,
        allowPurchase: Boolean(unit.allowPurchase),
      })) : [],
    };
    try {
      if (editing) {
        await api.put(`/inventory/${editing.id}`, payload);
      } else {
        await api.post('/inventory', payload);
        // Contribute to community catalog when a new product is saved with a scan barcode
        if (scanHit?.barcode && form.name) {
          api.post('/barcode/contribute', {
            barcode: scanHit.barcode,
            name: form.name,
            category: form.category || null,
            imageUrl: form.imageUrl || null,
          }).catch(() => {});
        }
      }
      setScanHit(null);
      setShowModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save item');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!canManageInventory) return;
    if (!confirm('Delete this item?')) return;
    await api.delete(`/inventory/${id}`);
    load();
  };

  // ── Excel import ─────────────────────────────────────────────────────────
  const downloadImportTemplate = async () => {
    const res = await api.get('/inventory/import/template', { responseType: 'blob' });
    const href = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = href;
    a.download = 'inventory-import-template.xlsx';
    a.click();
    URL.revokeObjectURL(href);
  };

  const handleImportFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportBusy(true);
    setImportError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/inventory/import/preview', formData);
      setImportState({
        rows: data.rows,
        summary: data.summary,
        excluded: new Set(data.rows.filter((r) => r.action === 'error').map((r) => r.rowNumber)),
        result: null,
      });
    } catch (err) {
      setImportError(err.response?.data?.error || 'Failed to read that file. Make sure it\'s a .xlsx export.');
    } finally {
      setImportBusy(false);
    }
  };

  const toggleImportRow = (rowNumber) => {
    setImportState((current) => {
      if (!current) return current;
      const excluded = new Set(current.excluded);
      if (excluded.has(rowNumber)) excluded.delete(rowNumber);
      else excluded.add(rowNumber);
      return { ...current, excluded };
    });
  };

  const closeImportModal = () => {
    if (importBusy) return;
    setImportState(null);
    setImportError('');
  };

  const confirmImport = async () => {
    if (!importState) return;
    const rowsToSend = importState.rows.filter((r) => r.action !== 'error' && !importState.excluded.has(r.rowNumber));
    if (!rowsToSend.length) return;
    setImportBusy(true);
    setImportError('');
    try {
      const { data } = await api.post('/inventory/import/commit', { rows: rowsToSend });
      setImportState((current) => ({ ...current, result: data }));
      load();
    } catch (err) {
      setImportError(err.response?.data?.error || 'Import failed. Please try again.');
    } finally {
      setImportBusy(false);
    }
  };

  const openAdjustmentModal = (item) => {
    if (!canAdjustInventory) return;
    const fallbackUnit = item.baseUnit || item.defaultSaleUnit || item.units?.[0] || null;
    const nextDirection = item.quantity > 0 ? 'decrease' : 'increase';
    setAdjustingItem(item);
    setAdjustmentForm({
      unitId: fallbackUnit?.id ? String(fallbackUnit.id) : '',
      direction: nextDirection,
      quantity: 1,
      reason: nextDirection === 'increase' ? 'count_gain' : 'damage',
      notes: '',
    });
    setAdjustmentError('');
  };

  const closeAdjustmentModal = (force = false) => {
    if (adjustmentLoading && !force) return;
    setAdjustingItem(null);
    setAdjustmentError('');
  };

  const handleAdjustmentSubmit = async (e) => {
    e.preventDefault();
    if (!adjustingItem) return;
    if (!canAdjustInventory) return;
    setAdjustmentLoading(true);
    setAdjustmentError('');
    try {
      await api.post('/stock-adjustments', {
        itemId: adjustingItem.id,
        unitId: adjustmentForm.unitId ? Number(adjustmentForm.unitId) : null,
        direction: adjustmentForm.direction,
        quantity: Number(adjustmentForm.quantity),
        reason: adjustmentForm.reason,
        notes: adjustmentForm.notes,
      });
      closeAdjustmentModal(true);
      await load();
    } catch (err) {
      setAdjustmentError(err.response?.data?.error || 'Failed to adjust stock');
    } finally {
      setAdjustmentLoading(false);
    }
  };

  const lowStockItems = items.filter((item) => item.quantity > 0 && item.quantity <= (item.reorderLevel ?? 10));
  const outOfStockItems = items.filter((item) => item.quantity === 0);
  const expiringSoonItems = items.filter((item) => {
    if (!item.expiryDate) return false;
    const days = Math.ceil((new Date(item.expiryDate) - Date.now()) / 86_400_000);
    return days >= 0 && days <= 30;
  });
  const expiredItems = items.filter((item) => item.expiryDate && new Date(item.expiryDate) < Date.now());
  const lowMarginItems = items.filter((item) => {
    const margin = getMarginPercent(item, canViewInventoryCosts);
    return margin !== null && margin < 15;
  });
  const stockValue = canViewInventoryCosts
    ? items.reduce((sum, item) => sum + getItemStockValue(item, canViewInventoryCosts), 0)
    : 0;
  const retailValue = items.reduce((sum, item) => sum + ((Number(item.price) || 0) * (Number(item.quantity) || 0)), 0);
  const categoriesCount = new Set(items.map((item) => item.category).filter(Boolean)).size;
  const supplierCoverageCount = items.filter((item) => item.supplierId || item.supplierName).length;

  const healthyItems = items.filter((item) => item.quantity > (item.reorderLevel ?? 10));
  const marginValues = canViewInventoryCosts
    ? items
        .map((item) => getMarginPercent(item, canViewInventoryCosts))
        .filter((value) => value !== null)
    : [];
  const averageMargin = marginValues.length
    ? Math.round(marginValues.reduce((sum, value) => sum + value, 0) / marginValues.length)
    : null;

  const filteredItems = items.filter((item) => {
    switch (stockFilter) {
      case 'low':
        if (!(item.quantity > 0 && item.quantity <= (item.reorderLevel ?? 10))) return false;
        break;
      case 'out':
        if (item.quantity !== 0) return false;
        break;
      case 'expiring': {
        if (!item.expiryDate) return false;
        const days = Math.ceil((new Date(item.expiryDate) - Date.now()) / 86_400_000);
        if (days > 30) return false;
        break;
      }
      case 'margin': {
        const margin = getMarginPercent(item, canViewInventoryCosts);
        if (!(margin !== null && margin < 15)) return false;
        break;
      }
      default:
        break;
    }

    if (categoryFilter !== 'all' && (item.category || 'Uncategorized') !== categoryFilter) return false;
    if (supplierFilter !== 'all' && (item.supplierName || 'No supplier') !== supplierFilter) return false;
    if (!priceMatchesBand(item.price, priceFilter)) return false;
    return true;
  });

  useEffect(() => {
    setSelectedIds((current) => current.filter((id) => filteredItems.some((item) => item.id === id)));
  }, [filteredItems]);

  const inventoryFilters = [
    { id: 'all', label: 'All', count: items.length, tone: 'gold' },
    { id: 'low', label: 'Low Stock', count: lowStockItems.length, tone: 'gold' },
    { id: 'out', label: 'Out', count: outOfStockItems.length, tone: 'coral' },
    { id: 'expiring', label: 'Expiring', count: expiringSoonItems.length + expiredItems.length, tone: 'gold' },
  ];

  if (canViewInventoryCosts) {
    inventoryFilters.push({ id: 'margin', label: 'Margin', count: lowMarginItems.length, tone: 'sky' });
  }

  const categoryOptions = ['all', ...new Set(items.map((item) => item.category || 'Uncategorized'))];
  const supplierOptions = ['all', ...new Set(items.map((item) => item.supplierName || 'No supplier'))];
  const selectedCount = selectedIds.length;
  const allVisibleSelected = filteredItems.length > 0 && filteredItems.every((item) => selectedIds.includes(item.id));
  const clearFilters = () => {
    setStockFilter('all');
    setCategoryFilter('all');
    setSupplierFilter('all');
    setPriceFilter('all');
  };
  const toggleSelectAll = () => {
    if (!filteredItems.length) return;
    if (allVisibleSelected) {
      setSelectedIds((current) => current.filter((id) => !filteredItems.some((item) => item.id === id)));
      return;
    }
    setSelectedIds((current) => [...new Set([...current, ...filteredItems.map((item) => item.id)])]);
  };
  const toggleSelected = (id) => {
    setSelectedIds((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  };
  const firstSelectedItem =
    filteredItems.find((item) => selectedIds.includes(item.id)) ||
    items.find((item) => selectedIds.includes(item.id));

  return (
    <Layout>
      <style>{INVENTORY_STYLES}</style>

      <div className="inventory-redesign-shell">
        <div className="inventory-redesign-inner">
          <section className="inventory-redesign-topbar">
            <div>
              <div className="inventory-redesign-eyebrow">Retail / Wholesale Control</div>
              <h1 className="inventory-redesign-title">
                Inventory <span>Control</span>
              </h1>
              <div className="inventory-redesign-alerts">
                {!canManageInventory && (
                  <div className="inventory-redesign-alert is-info">
                    <span className="inventory-redesign-dot" />
                    Read-only access. Stock changes still require a manager, admin, or owner.
                  </div>
                )}
                {(lowStockItems.length > 0 || outOfStockItems.length > 0) && (
                  <div className="inventory-redesign-alert is-warn">
                    <span className="inventory-redesign-dot" />
                    {lowStockItems.length + outOfStockItems.length} item{lowStockItems.length + outOfStockItems.length === 1 ? '' : 's'} need attention
                  </div>
                )}
              </div>
            </div>

            <div className="inventory-redesign-actions">
              <button type="button" className="inventory-redesign-btn" onClick={load}>
                Refresh
              </button>
              {canManageInventory && (
                <>
                  <input
                    ref={importInputRef}
                    type="file"
                    accept=".xlsx"
                    style={{ display: 'none' }}
                    onChange={handleImportFileSelected}
                  />
                  <button type="button" className="inventory-redesign-btn" onClick={downloadImportTemplate}>
                    Template
                  </button>
                  <button
                    type="button"
                    className="inventory-redesign-btn"
                    disabled={importBusy}
                    onClick={() => importInputRef.current?.click()}
                  >
                    {importBusy ? 'Reading file…' : 'Import Excel'}
                  </button>
                  <button type="button" className="inventory-redesign-btn is-gold" onClick={openNew}>
                    Add Item
                  </button>
                </>
              )}
            </div>
          </section>

          <section className="inventory-redesign-kpis">
            <article className="inventory-redesign-kpi is-gold">
              <div className="inventory-redesign-kpi-icon">SKU</div>
              <div className="inventory-redesign-kpi-label">Total Products</div>
              <div className="inventory-redesign-kpi-value is-gold">{items.length}</div>
              <div className="inventory-redesign-kpi-sub">Across {categoriesCount} categories</div>
            </article>

            <article className="inventory-redesign-kpi is-teal">
              <div className="inventory-redesign-kpi-icon">VAL</div>
              <div className="inventory-redesign-kpi-label">{canViewInventoryCosts ? 'Stock Value' : 'Retail Value'}</div>
              <div className="inventory-redesign-kpi-value is-teal">{fmt(canViewInventoryCosts ? stockValue : retailValue)}</div>
              <div className="inventory-redesign-kpi-sub">{canViewInventoryCosts ? `Retail side ${fmt(retailValue)}` : 'Sell-side view only'}</div>
            </article>

            <article className="inventory-redesign-kpi is-coral">
              <div className="inventory-redesign-kpi-icon">LOW</div>
              <div className="inventory-redesign-kpi-label">Low Stock</div>
              <div className="inventory-redesign-kpi-value is-coral">{lowStockItems.length}</div>
              <div className="inventory-redesign-kpi-sub">Below reorder level</div>
            </article>

            <article className="inventory-redesign-kpi is-lavender">
              <div className="inventory-redesign-kpi-icon">OUT</div>
              <div className="inventory-redesign-kpi-label">Out of Stock</div>
              <div className="inventory-redesign-kpi-value is-lavender">{outOfStockItems.length}</div>
              <div className="inventory-redesign-kpi-sub">Immediate restock pressure</div>
            </article>

            <article className={`inventory-redesign-kpi ${canViewInventoryCosts ? 'is-sky' : 'is-gold'}`}>
              <div className="inventory-redesign-kpi-icon">{canViewInventoryCosts ? 'MAR' : 'SUP'}</div>
              <div className="inventory-redesign-kpi-label">{canViewInventoryCosts ? 'Average Margin' : 'Supplier Coverage'}</div>
              <div className={`inventory-redesign-kpi-value ${canViewInventoryCosts ? 'is-sky' : 'is-gold'}`}>
                {canViewInventoryCosts ? `${averageMargin ?? 0}%` : supplierCoverageCount}
              </div>
              <div className="inventory-redesign-kpi-sub">
                {canViewInventoryCosts ? 'Across current catalog' : `${items.length - supplierCoverageCount} without supplier link`}
              </div>
              <div className="inventory-redesign-kpi-mini">
                <span>{healthyItems.length} healthy</span>
                <span>{expiringSoonItems.length + expiredItems.length} expiry risks</span>
              </div>
            </article>
          </section>

          <section className="inventory-redesign-controls">
            <div className="inventory-redesign-search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, SKU, or category..."
              />
            </div>

            <div className="inventory-redesign-select-wrap">
              <select className="inventory-redesign-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <option value="all">All categories</option>
                {categoryOptions.filter((value) => value !== 'all').map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>

            <div className="inventory-redesign-select-wrap">
              <select className="inventory-redesign-select" value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)}>
                <option value="all">All suppliers</option>
                {supplierOptions.filter((value) => value !== 'all').map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>

            <div className="inventory-redesign-select-wrap">
              <select className="inventory-redesign-select" value={priceFilter} onChange={(e) => setPriceFilter(e.target.value)}>
                <option value="all">Price: any</option>
                <option value="under20">Under GHS 20</option>
                <option value="20to50">GHS 20-50</option>
                <option value="over50">Over GHS 50</option>
              </select>
            </div>

            <div className="inventory-redesign-view-toggle">
              <button type="button" className={`inventory-redesign-icon-btn ${viewMode === 'table' ? 'is-active' : ''}`} onClick={() => setViewMode('table')} aria-label="Table view">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="6" rx="1.5" />
                  <rect x="3" y="14" width="18" height="6" rx="1.5" />
                </svg>
              </button>
              <button type="button" className={`inventory-redesign-icon-btn ${viewMode === 'grid' ? 'is-active' : ''}`} onClick={() => setViewMode('grid')} aria-label="Grid view">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="4" y="4" width="6" height="6" rx="1.5" />
                  <rect x="14" y="4" width="6" height="6" rx="1.5" />
                  <rect x="4" y="14" width="6" height="6" rx="1.5" />
                  <rect x="14" y="14" width="6" height="6" rx="1.5" />
                </svg>
              </button>
            </div>
          </section>

          <section className="inventory-redesign-tabs">
            {inventoryFilters.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={`inventory-redesign-tab ${stockFilter === filter.id ? 'is-active' : ''} ${filter.tone === 'coral' ? 'is-coral' : filter.tone === 'sky' ? 'is-teal' : ''}`}
                onClick={() => setStockFilter(filter.id)}
              >
                <span>{filter.label}</span>
                <span className="inventory-redesign-tab-count">{filter.count}</span>
              </button>
            ))}
          </section>

          {selectedCount > 0 && (
            <section className="inventory-redesign-bulkbar">
              <strong>{selectedCount} item{selectedCount === 1 ? '' : 's'} selected</strong>
              <div className="inventory-redesign-bulkbar-actions">
                {firstSelectedItem && canAdjustInventory && (
                  <button type="button" className="inventory-redesign-btn" onClick={() => openAdjustmentModal(firstSelectedItem)}>
                    Adjust first
                  </button>
                )}
                {firstSelectedItem && canManageInventory && (
                  <button type="button" className="inventory-redesign-btn" onClick={() => openEdit(firstSelectedItem)}>
                    Edit first
                  </button>
                )}
                <button type="button" className="inventory-redesign-btn" onClick={() => setSelectedIds([])}>
                  Clear selection
                </button>
              </div>
            </section>
          )}

          <section className="inventory-redesign-section">
            <div className="inventory-redesign-section-header">
              <div>
                <div className="inventory-redesign-section-title">Inventory Matrix</div>
                <div className="inventory-redesign-section-meta">
                  <span>{filteredItems.length} shown</span>
                  <span>{items.length} total</span>
                  <span>{viewMode === 'table' ? 'Table view' : 'Grid view'}</span>
                </div>
              </div>
              <div className="inventory-redesign-section-meta">
                <button type="button" className="inventory-redesign-btn" onClick={clearFilters}>
                  Reset filters
                </button>
              </div>
            </div>

            {pageLoading ? (
              <div className="inventory-redesign-loading">
                <div className="inventory-redesign-loading-list">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={`loading-${index}`} className="inventory-redesign-loading-row" />
                  ))}
                </div>
              </div>
            ) : pageError ? (
              <div className="inventory-redesign-error">
                <strong>Inventory load failed</strong>
                <div style={{ marginBottom: 16 }}>{pageError}</div>
                <button type="button" className="inventory-redesign-btn" onClick={load}>
                  Retry
                </button>
              </div>
            ) : items.length === 0 ? (
              <div className="inventory-redesign-empty">
                <strong>No products yet</strong>
                <div style={{ marginBottom: 16 }}>Add your first product to start tracking inventory.</div>
                {canManageInventory ? (
                  <button type="button" className="inventory-redesign-btn is-gold" onClick={openNew}>
                    Add your first product
                  </button>
                ) : (
                  <div>A manager, admin, or owner can add the first product.</div>
                )}
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="inventory-redesign-empty">
                <strong>No items in this view</strong>
                <div style={{ marginBottom: 16 }}>Try another filter or widen your search.</div>
                <button type="button" className="inventory-redesign-btn" onClick={clearFilters}>
                  Show everything
                </button>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="inventory-redesign-grid">
                {filteredItems.map((item) => {
                  const margin = getMarginPercent(item, canViewInventoryCosts);
                  const status = getItemStatus(item);
                  const stockPct = item.quantity === 0 ? 0 : Math.min((item.quantity / Math.max((item.reorderLevel ?? 10) * 3, 50)) * 100, 100);

                  return (
                    <article key={item.id} className="inventory-redesign-card">
                      <div className="inventory-redesign-card-top">
                        <div className="inventory-redesign-product">
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt={item.name} className="inventory-redesign-thumb" />
                          ) : (
                            <div className="inventory-redesign-thumb-empty">{productMonogram(item.name)}</div>
                          )}
                          <div>
                            <div className="inventory-redesign-product-name">{item.name}</div>
                            <div className="inventory-redesign-product-meta">
                              <span className="inventory-redesign-sku">{item.sku || 'No SKU'}</span>
                              {item.category && <span className="inventory-redesign-tag is-blue">{item.category}</span>}
                            </div>
                            <div className="inventory-redesign-product-subline">
                              Base {item.baseUnitName || 'Unit'} · Default sale {item.defaultSaleUnitName || item.baseUnitName || 'Unit'}
                            </div>
                          </div>
                        </div>

                        <label className="inventory-redesign-checkline">
                          <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => toggleSelected(item.id)} />
                          Select
                        </label>
                      </div>

                      <div>
                        <span className={`inventory-redesign-status ${status.tone === 'red' ? 'is-red' : status.tone === 'amber' ? 'is-amber' : 'is-green'}`}>
                          {status.label}
                        </span>
                      </div>

                      <div className="inventory-redesign-card-prices">
                        {canViewInventoryCosts && (
                          <div className="inventory-redesign-card-pricebox">
                            <label>Cost</label>
                            <div className="inventory-redesign-cost inventory-redesign-mono">{fmt(item.cost)}</div>
                          </div>
                        )}
                        <div className="inventory-redesign-card-pricebox">
                          <label>Selling</label>
                          <div className="inventory-redesign-price">{fmt(item.price)}</div>
                        </div>
                        <div className="inventory-redesign-card-pricebox">
                          <label>Stock</label>
                          <div className="inventory-redesign-stock">
                            <span className="inventory-redesign-stock-num">{item.quantity}</span>
                            <div className="inventory-redesign-stock-bar">
                              <div className="inventory-redesign-stock-fill" style={{ width: `${stockPct}%`, background: item.quantity === 0 ? 'var(--coral)' : item.quantity <= (item.reorderLevel ?? 10) ? 'var(--gold)' : 'var(--teal)' }} />
                            </div>
                          </div>
                        </div>
                        {canViewInventoryCosts && (
                          <div className="inventory-redesign-card-pricebox">
                            <label>Margin</label>
                            <div className={`inventory-redesign-margin ${margin === null ? 'is-muted' : margin < 15 ? 'is-thin' : margin < 25 ? 'is-ok' : 'is-good'}`}>
                              {margin === null ? '--' : `${Math.round(margin)}%`}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="inventory-redesign-product-subline">
                        Retail {fmt(resolveTierPrice({ retailPrice: item.baseUnit?.sellingPrice ?? item.price, pricingTier: 'retail' }))} · Wholesale {fmt(resolveTierPrice({ retailPrice: item.baseUnit?.sellingPrice ?? item.price, wholesalePrice: item.baseUnit?.wholesalePrice, pricingTier: 'wholesale' }))} · VIP {fmt(resolveTierPrice({ retailPrice: item.baseUnit?.sellingPrice ?? item.price, wholesalePrice: item.baseUnit?.wholesalePrice, vipPrice: item.baseUnit?.vipPrice, pricingTier: 'vip' }))}
                      </div>

                      <div className="inventory-redesign-card-actions">
                        {canAdjustInventory && (
                          <button type="button" className="inventory-redesign-action" onClick={() => openAdjustmentModal(item)}>
                            Adjust
                          </button>
                        )}
                        {canManageInventory && (
                          <button type="button" className="inventory-redesign-action" onClick={() => openEdit(item)}>
                            Edit
                          </button>
                        )}
                        {canManageInventory && (
                          <button type="button" className="inventory-redesign-action is-danger" onClick={() => handleDelete(item.id)}>
                            Delete
                          </button>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="inventory-redesign-table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>
                        <input type="checkbox" className="inventory-redesign-check" checked={allVisibleSelected} onChange={toggleSelectAll} />
                      </th>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Selling</th>
                      {canViewInventoryCosts && <th>Cost</th>}
                      {canViewInventoryCosts && <th>Margin</th>}
                      <th>Stock</th>
                      <th>Status</th>
                      <th>Expiry</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item) => {
                      const margin = getMarginPercent(item, canViewInventoryCosts);
                      const status = getItemStatus(item);
                      const stockPct = item.quantity === 0 ? 0 : Math.min((item.quantity / Math.max((item.reorderLevel ?? 10) * 3, 50)) * 100, 100);
                      const expiryLabel = item.expiryDate ? new Date(item.expiryDate).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' }) : '--';
                      const expiryDays = item.expiryDate ? Math.ceil((new Date(item.expiryDate) - Date.now()) / 86_400_000) : null;
                      const expiryTone = expiryDays == null ? 'is-green' : expiryDays < 0 ? 'is-red' : expiryDays <= 30 ? 'is-amber' : 'is-green';

                      return (
                        <tr key={item.id} className={`inventory-redesign-row ${item.quantity === 0 ? 'is-out' : item.quantity <= (item.reorderLevel ?? 10) ? 'is-low' : ''}`}>
                          <td>
                            <input type="checkbox" className="inventory-redesign-check" checked={selectedIds.includes(item.id)} onChange={() => toggleSelected(item.id)} />
                          </td>
                          <td>
                            <div className="inventory-redesign-product">
                              {item.imageUrl ? (
                                <img src={item.imageUrl} alt={item.name} className="inventory-redesign-thumb" />
                              ) : (
                                <div className="inventory-redesign-thumb-empty">{productMonogram(item.name)}</div>
                              )}
                              <div>
                                <div className="inventory-redesign-product-name">{item.name}</div>
                                <div className="inventory-redesign-product-meta">
                                  {item.category && <span className="inventory-redesign-tag is-blue">{item.category}</span>}
                                  {item.supplierName && <span className="inventory-redesign-tag is-muted">{item.supplierName}</span>}
                                </div>
                                <div className="inventory-redesign-product-subline">
                                  Base {item.baseUnitName || 'Unit'} · Default sale {item.defaultSaleUnitName || item.baseUnitName || 'Unit'} · {item.unitCount || 1} unit{(item.unitCount || 1) === 1 ? '' : 's'}
                                </div>
                                <div className="inventory-redesign-product-subline">
                                  Retail {fmt(resolveTierPrice({ retailPrice: item.baseUnit?.sellingPrice ?? item.price, pricingTier: 'retail' }))} · Wholesale {fmt(resolveTierPrice({ retailPrice: item.baseUnit?.sellingPrice ?? item.price, wholesalePrice: item.baseUnit?.wholesalePrice, pricingTier: 'wholesale' }))} · VIP {fmt(resolveTierPrice({ retailPrice: item.baseUnit?.sellingPrice ?? item.price, wholesalePrice: item.baseUnit?.wholesalePrice, vipPrice: item.baseUnit?.vipPrice, pricingTier: 'vip' }))}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="inventory-redesign-sku">{item.sku || 'No SKU'}</td>
                          <td className="inventory-redesign-price">{fmt(item.price)}</td>
                          {canViewInventoryCosts && <td className="inventory-redesign-cost">{fmt(item.cost)}</td>}
                          {canViewInventoryCosts && (
                            <td>
                              <span className={`inventory-redesign-margin ${margin === null ? 'is-muted' : margin < 15 ? 'is-thin' : margin < 25 ? 'is-ok' : 'is-good'}`}>
                                {margin === null ? '--' : `${Math.round(margin)}%`}
                              </span>
                            </td>
                          )}
                          <td>
                            <div className="inventory-redesign-stock">
                              <span className="inventory-redesign-stock-num">{item.quantity}</span>
                              <div className="inventory-redesign-stock-bar">
                                <div className="inventory-redesign-stock-fill" style={{ width: `${stockPct}%`, background: item.quantity === 0 ? 'var(--coral)' : item.quantity <= (item.reorderLevel ?? 10) ? 'var(--gold)' : 'var(--teal)' }} />
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`inventory-redesign-status ${status.tone === 'red' ? 'is-red' : status.tone === 'amber' ? 'is-amber' : 'is-green'}`}>
                              {status.label}
                            </span>
                          </td>
                          <td>
                            <div className="inventory-redesign-expiry">
                              <span className="inventory-redesign-expiry-date">{expiryLabel}</span>
                              {expiryDays !== null && (
                                <span className={`inventory-redesign-expiry-badge ${expiryTone}`}>
                                  {expiryDays < 0 ? `${Math.abs(expiryDays)}d ago` : `${expiryDays}d left`}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="inventory-redesign-actions-cell">
                              {canAdjustInventory && (
                                <button type="button" className="inventory-redesign-action" onClick={() => openAdjustmentModal(item)}>
                                  Adjust
                                </button>
                              )}
                              {canManageInventory && (
                                <button type="button" className="inventory-redesign-action" onClick={() => openEdit(item)}>
                                  Edit
                                </button>
                              )}
                              {canManageInventory && (
                                <button type="button" className="inventory-redesign-action is-danger" onClick={() => handleDelete(item.id)}>
                                  Delete
                                </button>
                              )}
                              {!canAdjustInventory && !canManageInventory && (
                                <span className="inventory-redesign-action is-ghost">Read only</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {canViewInventoryActivity && (
            <section className="inventory-redesign-section">
              <div className="inventory-redesign-section-header">
                <div>
                  <div className="inventory-redesign-section-title">Recent Stock Activity</div>
                  <div className="inventory-redesign-section-meta">
                    <span>{adjustments.length} recent movement{adjustments.length === 1 ? '' : 's'}</span>
                  </div>
                </div>
              </div>

              {adjustments.length > 0 ? (
                <div className="inventory-redesign-activity">
                  {adjustments.map((entry) => (
                    <div key={entry.id} className="inventory-redesign-activity-item">
                      <div className={`inventory-redesign-activity-icon ${entry.direction === 'increase' ? 'is-increase' : 'is-decrease'}`}>
                        {entry.direction === 'increase' ? '+' : '-'}
                      </div>
                      <div>
                        <div className="inventory-redesign-activity-title">
                          {entry.item?.name || 'Item'} · {entry.quantity} {String(entry.unitName || 'Unit').toLowerCase()}{entry.quantity === 1 ? '' : 's'}
                        </div>
                        <div className="inventory-redesign-activity-meta">
                          {adjustmentReasonLabel(entry.reason)} · {entry.baseQuantity} base units · {entry.actorUser?.name || 'System'} · {new Date(entry.createdAt).toLocaleString('en-GH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      <div className="inventory-redesign-activity-trail">
                        {entry.saleReturn?.saleId ? `Sale #${entry.saleReturn.saleId}` : entry.direction}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="inventory-redesign-empty">
                  <strong>No stock adjustments yet</strong>
                  <div>Adjustments and customer returns will appear here.</div>
                </div>
              )}
            </section>
          )}
        </div>
      </div>

        {showModal && (
          <div
            className="modal-overlay"
            onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
          >
            <div className="modal" style={{ animation: 'modalEnter 300ms cubic-bezier(0.34,1.56,0.64,1) forwards', maxWidth: 560 }}>
              <div className="modal-header">
                <h3 className="modal-title">{editing ? 'Edit Item' : 'Add New Item'}</h3>
                <button className="btn btn-outline btn-sm" onClick={() => setShowModal(false)}>✕</button>
              </div>
              {error && <div className="alert alert-error">{error}</div>}
              <form onSubmit={handleSubmit}>
                {!editing && (
                  <div style={{ marginBottom: 16 }}>
                    <button
                      type="button"
                      onClick={() => setShowScanner(true)}
                      disabled={scanLoading}
                      style={{
                        width: '100%', padding: '13px',
                        borderRadius: 10,
                        background: 'rgba(240,180,41,0.07)',
                        border: '2px dashed rgba(240,180,41,0.35)',
                        color: '#F0B429', fontSize: 14, fontWeight: 700,
                        cursor: scanLoading ? 'default' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      }}
                    >
                      <span style={{ fontSize: 20 }}>📷</span>
                      {scanLoading ? 'Looking up product…' : 'Scan Barcode to Auto-Fill'}
                    </button>

                    {scanHit && (
                      <div style={{
                        marginTop: 8, padding: '9px 13px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                        ...(scanHit.source
                          ? { background: 'rgba(0,201,177,0.08)', border: '1px solid rgba(0,201,177,0.2)', color: '#00C9B1' }
                          : { background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.2)', color: '#94A3B8' }),
                      }}>
                        {scanHit.source
                          ? `✓ Auto-filled from ${scanHit.source}${scanHit.brand ? ` · ${scanHit.brand}` : ''} · Add your price and stock`
                          : 'Barcode not found — fill in the details and we\'ll save it for all OptiKa users'}
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Product Name *</label>
                  <input className="form-input" value={form.name} onChange={set('name')} required placeholder="e.g. Wireless Headphones" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">SKU</label>
                    <input className="form-input" value={form.sku} onChange={set('sku')} placeholder="e.g. SKU-001" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <input className="form-input" value={form.category} onChange={set('category')} placeholder="e.g. Electronics" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Cost Price (GHS) *</label>
                    <input className="form-input" type="number" step="0.01" min="0" value={form.cost} onChange={set('cost')} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Retail Price (GHS) *</label>
                    <input className="form-input" type="number" step="0.01" min="0" value={form.price} onChange={set('price')} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Wholesale Price (GHS)</label>
                    <input className="form-input" type="number" step="0.01" min="0" value={form.wholesalePrice} onChange={set('wholesalePrice')} placeholder="Defaults to retail price" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">VIP Price (GHS)</label>
                    <input className="form-input" type="number" step="0.01" min="0" value={form.vipPrice} onChange={set('vipPrice')} placeholder="Defaults to wholesale or retail" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Quantity in Stock</label>
                  <input className="form-input" type="number" min="0" value={form.quantity} onChange={set('quantity')} />
                </div>

                <div className="form-group" style={{ border: '1px solid var(--border)', borderRadius: 16, padding: 16, background: 'rgba(255,255,255,0.03)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                    <div>
                      <div className="form-label" style={{ marginBottom: 4 }}>Units & Pricing</div>
                      <div style={{ fontSize: '0.74rem', color: 'var(--muted)' }}>
                        Keep one product, then define how it is sold as unit, pack, carton, or box.
                      </div>
                    </div>
                    <button type="button" className="btn btn-outline btn-sm" onClick={addUnit}>
                      + Add unit
                    </button>
                  </div>

                  <div style={{ border: '1px solid var(--border)', background: 'rgba(11,14,23,0.26)', borderRadius: 14, padding: 14, marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 800, color: 'var(--white)' }}>Base unit</div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: 'var(--muted)', fontWeight: 700 }}>
                        <input
                          type="radio"
                          name="defaultSaleUnit"
                          checked={form.defaultSaleUnitKey === 'base'}
                          onChange={() => setForm((current) => ({ ...current, defaultSaleUnitKey: 'base' }))}
                        />
                        Default sale unit
                      </label>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Unit name</label>
                        <input className="form-input" value={form.baseUnitName} onChange={set('baseUnitName')} placeholder="e.g. Bottle" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">How it works</label>
                        <div style={{ minHeight: 44, borderRadius: 12, border: '1px dashed rgba(255,255,255,0.16)', background: 'rgba(255,255,255,0.02)', padding: '10px 12px', fontSize: '0.74rem', color: 'var(--muted)', display: 'flex', alignItems: 'center' }}>
                          Uses the stock quantity, SKU, cost price, and tiered prices above.
                        </div>
                      </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.74rem', fontWeight: 700, color: 'var(--muted)' }}>
                      <input type="checkbox" checked={form.baseUnitAllowPurchase} onChange={setFlag('baseUnitAllowPurchase')} />
                      Allow purchasing in this base unit
                    </label>
                  </div>

                  {form.units.length > 0 ? (
                    <div style={{ display: 'grid', gap: 12 }}>
                      {form.units.map((unit, index) => (
                        <div key={unit.key} style={{ border: '1px solid var(--border)', background: 'rgba(11,14,23,0.26)', borderRadius: 14, padding: 14 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 800, color: 'var(--white)' }}>
                              Extra unit {index + 1}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: 'var(--muted)', fontWeight: 700 }}>
                                <input
                                  type="radio"
                                  name="defaultSaleUnit"
                                  checked={form.defaultSaleUnitKey === unit.key}
                                  disabled={!unit.allowSale}
                                  onChange={() => setForm((current) => ({ ...current, defaultSaleUnitKey: unit.key }))}
                                />
                                Default sale
                              </label>
                              <button type="button" className="btn btn-danger btn-sm" onClick={() => removeUnit(unit.key)}>
                                Remove
                              </button>
                            </div>
                          </div>
                          <div className="form-row">
                            <div className="form-group">
                              <label className="form-label">Unit name</label>
                              <input
                                className="form-input"
                                value={unit.name}
                                onChange={(e) => updateUnit(unit.key, 'name', e.target.value)}
                                placeholder="e.g. Carton"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Base units inside</label>
                              <input
                                className="form-input"
                                type="number"
                                min="2"
                                value={unit.conversionQty}
                                onChange={(e) => updateUnit(unit.key, 'conversionQty', e.target.value)}
                                placeholder="e.g. 24"
                              />
                            </div>
                          </div>
                          <div className="form-row">
                            <div className="form-group">
                              <label className="form-label">Retail price</label>
                              <input
                                className="form-input"
                                type="number"
                                step="0.01"
                                min="0"
                                value={unit.sellingPrice}
                                onChange={(e) => updateUnit(unit.key, 'sellingPrice', e.target.value)}
                                placeholder="e.g. 120"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Wholesale price</label>
                              <input
                                className="form-input"
                                type="number"
                                step="0.01"
                                min="0"
                                value={unit.wholesalePrice}
                                onChange={(e) => updateUnit(unit.key, 'wholesalePrice', e.target.value)}
                                placeholder="Defaults to retail"
                              />
                            </div>
                          </div>
                          <div className="form-row">
                            <div className="form-group">
                              <label className="form-label">VIP price</label>
                              <input
                                className="form-input"
                                type="number"
                                step="0.01"
                                min="0"
                                value={unit.vipPrice}
                                onChange={(e) => updateUnit(unit.key, 'vipPrice', e.target.value)}
                                placeholder="Defaults to wholesale or retail"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Cost price</label>
                              <input
                                className="form-input"
                                type="number"
                                step="0.01"
                                min="0"
                                value={unit.costPrice}
                                onChange={(e) => updateUnit(unit.key, 'costPrice', e.target.value)}
                                placeholder="Optional"
                              />
                            </div>
                          </div>
                          <div className="form-row">
                            <div className="form-group">
                              <label className="form-label">Unit SKU (optional)</label>
                              <input
                                className="form-input"
                                value={unit.sku}
                                onChange={(e) => updateUnit(unit.key, 'sku', e.target.value)}
                                placeholder="e.g. WATER-CTN"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Availability</label>
                              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingTop: 10 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.74rem', color: 'var(--muted)', fontWeight: 700 }}>
                                  <input
                                    type="checkbox"
                                    checked={unit.allowSale}
                                    onChange={(e) => updateUnit(unit.key, 'allowSale', e.target.checked)}
                                  />
                                  Sell this unit
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.74rem', color: 'var(--muted)', fontWeight: 700 }}>
                                  <input
                                    type="checkbox"
                                    checked={unit.allowPurchase}
                                    onChange={(e) => updateUnit(unit.key, 'allowPurchase', e.target.checked)}
                                  />
                                  Buy this unit
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ border: '1px dashed rgba(255,255,255,0.16)', borderRadius: 14, padding: '14px 16px', background: 'rgba(255,255,255,0.02)', color: 'var(--muted)', fontSize: '0.75rem' }}>
                      Add pack, box, or carton levels when you want to buy or sell the same product in larger quantities.
                    </div>
                  )}
                </div>

                {/* ── New intelligence fields ── */}
                <div className="form-group">
                  <label className="form-label">Expiry Date (optional)</label>
                  <input className="form-input" type="date" value={form.expiryDate} onChange={set('expiryDate')} />
                  <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginTop: 4 }}>Leave blank for non-perishable items</div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Low Stock Alert At</label>
                    <input className="form-input" type="number" min="0" value={form.reorderLevel} onChange={set('reorderLevel')} />
                    <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginTop: 4 }}>Alert when stock drops below this number</div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Reorder Quantity</label>
                    <input className="form-input" type="number" min="0" value={form.reorderQuantity} onChange={set('reorderQuantity')} />
                    <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginTop: 4 }}>How many units to order when restocking</div>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Supplier (optional)</label>
                  <select className="form-input" value={form.supplierId} onChange={set('supplierId')}>
                    <option value="">— No supplier assigned —</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div className="inventory-redesign-upload">
                  <ImageUpload
                    value={form.imageUrl || null}
                    onChange={(url) => setForm((f) => ({ ...f, imageUrl: url || '' }))}
                    onPathChange={(path) => setForm((f) => ({ ...f, imagePath: path || '' }))}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="form-input" rows={2} value={form.description} onChange={set('description')} style={{ resize: 'vertical' }} />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline" onClick={() => { setShowModal(false); setScanHit(null); }}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading
                      ? <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 14, height: 14, border: '2px solid rgba(0,0,0,0.15)', borderTopColor: '#0F172A', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
                          Saving...
                        </span>
                      : editing ? 'Save Changes' : 'Add Item'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showScanner && (
          <BarcodeScanner
            onResult={handleBarcodeScan}
            onClose={() => setShowScanner(false)}
          />
        )}

        {adjustingItem && (
          <div
            className="modal-overlay"
            onClick={(e) => e.target === e.currentTarget && closeAdjustmentModal()}
          >
            <div className="modal" style={{ maxWidth: 480 }}>
              <div className="modal-header">
                <h3 className="modal-title">Adjust Stock</h3>
                <button className="btn btn-outline btn-sm" onClick={closeAdjustmentModal}>✕</button>
              </div>
              {adjustmentError && <div className="alert alert-error">{adjustmentError}</div>}
              <form onSubmit={handleAdjustmentSubmit}>
                <div style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 14, background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 800, color: 'var(--white)' }}>{adjustingItem.name}</div>
                  <div style={{ fontSize: '0.76rem', color: 'var(--muted)', marginTop: 4 }}>
                    Current stock: {adjustingItem.quantity} {String(adjustingItem.baseUnitName || 'base units').toLowerCase()}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Direction</label>
                    <select
                      className="form-input"
                      value={adjustmentForm.direction}
                      onChange={(e) => setAdjustmentForm((current) => ({
                        ...current,
                        direction: e.target.value,
                        reason: ADJUSTMENT_REASONS[e.target.value]?.[0]?.id || current.reason,
                      }))}
                    >
                      <option value="decrease">Reduce stock</option>
                      <option value="increase">Add stock</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unit</label>
                    <select
                      className="form-input"
                      value={adjustmentForm.unitId}
                      onChange={(e) => setAdjustmentForm((current) => ({ ...current, unitId: e.target.value }))}
                    >
                      {(adjustingItem.units?.length ? adjustingItem.units : [adjustingItem.baseUnit]).filter(Boolean).map((unit) => (
                        <option key={unit.id || unit.name} value={unit.id || ''}>
                          {unit.name} · {unit.conversionQty || 1} {String(adjustingItem.baseUnitName || 'unit').toLowerCase()}{Number(unit.conversionQty || 1) === 1 ? '' : 's'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Quantity</label>
                    <input
                      className="form-input"
                      type="number"
                      min="1"
                      value={adjustmentForm.quantity}
                      onChange={(e) => setAdjustmentForm((current) => ({ ...current, quantity: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Reason</label>
                    <select
                      className="form-input"
                      value={adjustmentForm.reason}
                      onChange={(e) => setAdjustmentForm((current) => ({ ...current, reason: e.target.value }))}
                    >
                      {(ADJUSTMENT_REASONS[adjustmentForm.direction] || []).map((reason) => (
                        <option key={reason.id} value={reason.id}>{reason.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-input"
                    rows={2}
                    value={adjustmentForm.notes}
                    onChange={(e) => setAdjustmentForm((current) => ({ ...current, notes: e.target.value }))}
                    style={{ resize: 'vertical' }}
                    placeholder="Optional note for this stock change"
                  />
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-outline" onClick={closeAdjustmentModal}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={adjustmentLoading}>
                    {adjustmentLoading ? 'Saving...' : 'Save adjustment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {(importState || importError) && (
          <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && closeImportModal()}>
            <div className="modal" style={{ maxWidth: 900 }}>
              <div className="modal-header">
                <h3 className="modal-title">
                  {importState?.result ? 'Import Complete' : 'Import Inventory from Excel'}
                </h3>
                <button className="btn btn-outline btn-sm" onClick={closeImportModal} disabled={importBusy}>✕</button>
              </div>

              {importError && <div className="alert alert-error">{importError}</div>}

              {importState?.result && (
                <>
                  <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
                    <span className="badge badge-green">{importState.result.created} created</span>
                    <span className="badge badge-blue">{importState.result.updated} updated</span>
                    {importState.result.failed.length > 0 && (
                      <span className="badge badge-red">{importState.result.failed.length} failed</span>
                    )}
                  </div>
                  {importState.result.failed.length > 0 && (
                    <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 10, marginBottom: 8 }}>
                      <table>
                        <thead><tr><th>Row</th><th>Product</th><th>Reason</th></tr></thead>
                        <tbody>
                          {importState.result.failed.map((f) => (
                            <tr key={f.rowNumber}>
                              <td>{f.rowNumber}</td>
                              <td>{f.name || '—'}</td>
                              <td>{f.error}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="modal-footer">
                    <button type="button" className="btn btn-primary" onClick={closeImportModal}>Done</button>
                  </div>
                </>
              )}

              {importState && !importState.result && (
                <>
                  <div style={{ marginBottom: 14, fontSize: '0.85rem', color: 'var(--muted)' }}>
                    {importState.summary.create} new item{importState.summary.create === 1 ? '' : 's'} · {importState.summary.update} update{importState.summary.update === 1 ? '' : 's'}
                    {importState.summary.error > 0 && <> · {importState.summary.error} row{importState.summary.error === 1 ? '' : 's'} with errors (will be skipped)</>}.
                    {' '}Uncheck any row you don't want to import.
                  </div>
                  <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 10, marginBottom: 4 }}>
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: 32 }}></th>
                          <th>Row</th>
                          <th>Product</th>
                          <th>Category</th>
                          <th>Qty</th>
                          <th>Unit</th>
                          <th>Cost</th>
                          <th>Sell</th>
                          <th>SKU</th>
                          <th>Supplier</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importState.rows.map((r) => {
                          const isError = r.action === 'error';
                          const excluded = importState.excluded.has(r.rowNumber);
                          return (
                            <tr key={r.rowNumber} style={isError ? { opacity: 0.6 } : undefined}>
                              <td>
                                <input
                                  type="checkbox"
                                  disabled={isError}
                                  checked={!excluded}
                                  onChange={() => toggleImportRow(r.rowNumber)}
                                />
                              </td>
                              <td>{r.rowNumber}</td>
                              <td>{r.name || '—'}</td>
                              <td>{r.category || '—'}</td>
                              <td>{r.quantity}</td>
                              <td>{r.unit || 'Unit'}</td>
                              <td>{fmt(r.cost)}</td>
                              <td>{fmt(r.price)}</td>
                              <td>{r.sku || '—'}</td>
                              <td>{r.supplier || '—'}</td>
                              <td>
                                {isError
                                  ? <span className="badge badge-red" title={r.errors.join('; ')}>{r.errors[0]}</span>
                                  : <span className={`badge ${r.action === 'update' ? 'badge-blue' : 'badge-green'}`}>{r.action}</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
                    <button type="button" className="btn btn-outline btn-sm" onClick={downloadImportTemplate}>Download Template</button>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button type="button" className="btn btn-outline" onClick={closeImportModal} disabled={importBusy}>Cancel</button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={confirmImport}
                        disabled={importBusy || importState.rows.filter((r) => r.action !== 'error' && !importState.excluded.has(r.rowNumber)).length === 0}
                      >
                        {importBusy ? 'Importing…' : `Import ${importState.rows.filter((r) => r.action !== 'error' && !importState.excluded.has(r.rowNumber)).length} item(s)`}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {!importState && importError && (
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline" onClick={closeImportModal}>Close</button>
                </div>
              )}
            </div>
          </div>
        )}
    </Layout>
  );
}
